/**
 * ForgeSelector
 * 
 * Dropdown component for selecting a Forge to map to a Domain.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import forgeService from '../../services/forgeService';
import { Forge } from '../../types';

interface ForgeSelectorProps {
  clientId?: string | null;  // Filter forges by client ID (takes precedence over organizationId)
  organizationId?: string | null;  // Resolve client ID from the organization's forge_client_id
  selectedForgeId?: string | null;
  onSelect: (forgeId: string | null, forge: Forge | null) => void;
  disabled?: boolean;
  className?: string;
}

export const ForgeSelector: React.FC<ForgeSelectorProps> = ({
  clientId,
  organizationId,
  selectedForgeId,
  onSelect,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const [forges, setForges] = useState<Forge[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgHasClient, setOrgHasClient] = useState<boolean>(true);

  useEffect(() => {
    const fetchForges = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let effectiveClientId = clientId || undefined;
        // If we don't have an explicit clientId but have an organizationId,
        // resolve the org's forge_client_id first to scope the listing.
        if (!effectiveClientId && organizationId) {
          const mapping = await forgeService.getOrganizationClientMapping(organizationId);
          if (!mapping.forge_client_id) {
            setOrgHasClient(false);
            setForges([]);
            return;
          }
          effectiveClientId = mapping.forge_client_id;
        }
        setOrgHasClient(true);
        const data = await forgeService.listForges(effectiveClientId);
        setForges(data);
      } catch (err: any) {
        setError(err?.message || t('forge.selector.errorLoadingForges', 'Failed to load Forges'));
        console.error('Error fetching Forges:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchForges();
  }, [clientId, organizationId, t]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      onSelect(null, null);
    } else {
      const forge = forges.find(f => f.forge_id === value);
      onSelect(value, forge || null);
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-muted-foreground mb-1">
        {t('forge.selector.forgeLabel', 'Forge')}
      </label>
      <select
        value={selectedForgeId || ''}
        onChange={handleChange}
        disabled={disabled || isLoading}
        className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50"
      >
        <option value="">
          {isLoading
            ? t('global.loading', 'Loading...')
            : t('forge.selector.selectForge', '-- Select a Forge --')}
        </option>
        {forges.map((forge) => (
          <option key={forge.forge_id} value={forge.forge_id}>
            {forge.display_name || forge.name}
          </option>
        ))}
      </select>
      {error && (
        <p className="text-xs text-destructive mt-1">{error}</p>
      )}
      {!error && !isLoading && organizationId && !orgHasClient && (
        <p className="text-xs text-muted-foreground mt-1">
          {t(
            'forge.selector.orgHasNoClient',
            "This organization is not linked to a Forge client. Ask a super-admin to map it first."
          )}
        </p>
      )}
    </div>
  );
};

export default ForgeSelector;
